import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SquareArrowOutUpRight, Heart, Sparkles, HeartPulse, Gem, Users, Shield, Plus } from 'lucide-react';

const DEDICATION_OPTIONS = [
  { id: 'refuah', label: 'Refuah', icon: Heart, subtitle: 'Healing and recovery' },
  { id: 'memory', label: 'In Memory of', icon: Sparkles, subtitle: 'Honoring a loved one' },
  { id: 'health', label: 'Health', icon: HeartPulse, subtitle: 'Physical wellbeing' },
  { id: 'success', label: 'Success', icon: Gem, subtitle: 'Achievement and prosperity' },
  { id: 'zivug', label: 'Zivug', icon: Users, subtitle: 'Finding a life partner' },
  { id: 'soldiers', label: 'Soldiers', icon: Shield, subtitle: 'Protection and safety' },
  { id: 'other', label: 'Other', icon: Plus, subtitle: 'Custom intention' }
];

export default function SelectDedicationPage() {
  const navigate = useNavigate();
  const [selectedDedication, setSelectedDedication] = useState(null);
  const [customDedication, setCustomDedication] = useState('');

  // Device detection for app store redirect
  const handleAppStoreRedirect = () => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    let appStoreUrl;
    
    if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
      appStoreUrl = 'https://apps.apple.com/us/app/yesod/id6748472269';
    } else if (/android/i.test(userAgent)) {
      appStoreUrl = 'https://play.google.com/store/apps/details?id=us.yesod.app';
    } else {
      appStoreUrl = 'https://apps.apple.com/us/app/yesod/id6748472269';
    }
    
    window.open(appStoreUrl, '_blank');
  };

  const handleSelect = (dedicationId) => {
    setSelectedDedication(dedicationId);
    if (dedicationId !== 'other') {
      setCustomDedication('');
    }
  };

  const handleNext = () => {
    if (selectedDedication) {
      // Store dedication in sessionStorage
      const dedicationToStore = selectedDedication === 'other' && customDedication
        ? customDedication
        : selectedDedication;
      sessionStorage.setItem('chainDedication', dedicationToStore);
      // Navigate to Step 2: Choose Chain Type
      navigate('/create-chain/type');
    }
  };

  const isNextDisabled = !selectedDedication || (selectedDedication === 'other' && !customDedication.trim());

  const handleCancel = () => {
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-white flex flex-col page-slide-up">
      {/* Header */}
      <div className="sticky top-0 z-50 flex items-center justify-between px-5 py-3 border-b border-gray-200 bg-white gap-4">
        <div className="flex items-center gap-3 flex-shrink-0">
          <img 
            src="https://xvhellooihpelwifxuik.supabase.co/storage/v1/object/public/assets/Yesod%20Logo%204K%20(1).png" 
            alt="Yesod" 
            className="h-10 w-auto object-contain flex-shrink-0"
          />
          <h1 className="text-xl font-bold text-black m-0 flex-shrink-0">Yesod</h1>
        </div>
        <button 
          onClick={handleAppStoreRedirect}
          className="bg-[#667eea] text-white px-4 py-2 rounded-lg text-sm font-semibold whitespace-nowrap flex-shrink-0 ml-auto flex items-center gap-1.5 hover:bg-[#5568d3] transition-all cursor-pointer"
        >
          Continue in App
          <SquareArrowOutUpRight size={16} />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 px-5 py-8 pb-32">
        <h2 className="text-3xl font-bold text-[#667eea] mb-3 leading-tight">Select a Dedication</h2>
        <p className="text-base text-gray-600 mb-8 leading-relaxed">Choose an intention to start your prayer chain</p>

        {/* Dedication Grid */}
        <div className="grid grid-cols-2 gap-4 max-w-2xl mx-auto">
          {DEDICATION_OPTIONS.map((option) => {
            const IconComponent = option.icon;
            const isSelected = selectedDedication === option.id;
            return (
              <button
                key={option.id}
                className={`bg-gray-50 border-2 rounded-2xl px-4 py-8 flex flex-col items-center justify-center gap-3 cursor-pointer transition-all min-h-[140px] relative ${
                  isSelected
                    ? 'bg-blue-50 border-[#667eea] border-[3px] shadow-[0_0_0_3px_rgba(102,126,234,0.1)]'
                    : 'border-gray-200 hover:bg-gray-100 hover:border-gray-300 hover:translate-y-[-2px] hover:shadow-md'
                }`}
                onClick={() => handleSelect(option.id)}
              >
                <div className={`text-5xl flex items-center justify-center mb-2 transition-colors ${
                  isSelected ? 'text-[#667eea]' : 'text-gray-400'
                }`}>
                  <IconComponent size={32} strokeWidth={2} />
                </div>
                <div className="text-base font-semibold text-gray-900 text-center leading-snug">{option.label}</div>
                {selectedDedication === 'other' && option.id === 'other' && (
                  <input
                    type="text"
                    className="w-full mt-3 px-3 py-2.5 text-sm border-2 border-[#667eea] rounded-lg bg-white text-gray-900 outline-none transition-all placeholder:text-gray-400 focus:border-[#5a67d8] focus:shadow-[0_0_0_3px_rgba(102,126,234,0.1)]"
                    placeholder="Enter custom intention..."
                    value={customDedication}
                    onChange={(e) => setCustomDedication(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    autoFocus
                  />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Footer with Progress and Actions */}
      <div className="sticky bottom-0 left-0 right-0 px-5 py-5 border-t border-gray-200 bg-white flex flex-col gap-4 z-40 mt-auto">
        {/* Progress Indicator */}
        <div className="flex items-center justify-center gap-2">
          <div className="w-8 h-2 rounded bg-[#667eea] transition-all"></div>
          <div className="w-2 h-2 rounded-full bg-gray-300 transition-all"></div>
          <div className="w-2 h-2 rounded-full bg-gray-300 transition-all"></div>
          <div className="w-2 h-2 rounded-full bg-gray-300 transition-all"></div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-between gap-3">
          <button className="bg-transparent text-gray-700 underline hover:text-gray-900 transition-colors" onClick={handleCancel}>
            Cancel
          </button>
          <button
            className="px-6 py-2.5 text-sm font-semibold rounded-lg cursor-pointer transition-all border-none flex items-center justify-center bg-gradient-to-br from-[#667eea] to-[#764ba2] text-white hover:translate-y-[-2px] hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            onClick={handleNext}
            disabled={isNextDisabled}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}
